import * as React from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import DataSaverOffIcon from "@mui/icons-material/DataSaverOff";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import Collapse from "@mui/material/Collapse";
import { LoadingButton } from "@mui/lab";
import SendIcon from "@mui/icons-material/Send";
import FirstSettings from "../stages/FirstSettings";
import MediaPlan from "../stages/MediaPlan";
import Audience from "../stages/Audience";
import Optimization from "../stages/Optimization";
import { selectCreateCampaigns } from "../../../redux/createNewCompany/selectors";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ads } from "../stages/Banners/newBanner/Ads";
import { RetargetingPage } from "../stages/Retargeting";
import useUser from "../../../data/useUser";
import useAdminUser from "../../../data/useAdminUser";
import { useDispatch } from "react-redux";
import { setCurrentPage } from "../../../redux/pageContext/actions";
import { useSnackbar } from "notistack";
import { client } from "../../../utils/network";

const LAST_TAB = 7;

const DEMO_FEED_CATEGORIES = [
  { id: "demo-1", name: "Электроника и гаджеты" },
  { id: "demo-2", name: "Одежда и обувь" },
  { id: "demo-3", name: "Дом и сад" },
  { id: "demo-4", name: "Красота и уход" },
  { id: "demo-5", name: "Спорт и отдых" },
];

function getFeedStatusMeta(moderateStatus) {
  if (moderateStatus === "REJECTED") {
    return { label: "Отклонено", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" };
  }
  if (moderateStatus === "MODERATING") {
    return { label: "На модерации", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa" };
  }
  if (moderateStatus === "ACCEPTED") {
    return { label: "Активно", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" };
  }
  return { label: "Выключено", color: "#52525b", bg: "#f4f4f5", border: "#e4e4e7" };
}

function truncateFeedUrl(url, maxLength = 72) {
  if (!url) return "";
  if (url.length <= maxLength) return url;
  return `${url.slice(0, maxLength - 3)}...`;
}

function ProductFeedSection({
  campaignId,
  disable,
  demoMode,
  ymlOriginalFeedUrl,
  ymlReferralFeedUrl,
  campaignBroadcastStatus,
  campaignStatus,
  campaignIssues,
  ymlFeedModerateStatus,
  onNext,
  allowResendToModeration,
  showNextButton,
}) {
  const { role: adminPanelRole } = useAdminUser();
  const { enqueueSnackbar } = useSnackbar();
  const [feedUrl, setFeedUrl] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [categories, setCategories] = React.useState([]);
  const [referralFeedUrl, setReferralFeedUrl] = React.useState("");
  const [resendLoading, setResendLoading] = React.useState(false);
  const manageTimerRef = React.useRef(null);
  const pendingManageRef = React.useRef({});
  const ymlBootstrapDoneRef = React.useRef(false);
  const [categoriesExpanded, setCategoriesExpanded] = React.useState(false);

  const showAdminRedirect =
    ["ADMIN", "SUPERUSER"].includes(adminPanelRole);

  const w = typeof window !== "undefined" ? window.outerWidth : 1200;
  const styles = {
    container: {
      marginLeft: "20px",
      marginTop: w < 768 ? "40px" : "0",
      width: w < 375 ? "288px" : w < 768 ? "343px" : "auto",
      maxWidth: w >= 1000 ? 960 : undefined,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    title: {
      marginBottom: "10px",
      fontSize: "16px",
      fontWeight: "600",
      color: "#1a1a1a",
    },
    infoNotification: {
      backgroundColor: "#eff6ff",
      border: "1px solid #bfdbfe",
      borderRadius: "8px",
      padding: "12px 16px",
      marginBottom: "24px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    infoNotificationContent: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    infoText: {
      fontSize: "14px",
      color: "#1e40af",
      lineHeight: "1.5",
    },
    card: {
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "20px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#1f2937",
    },
    switchContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
      gap: "12px",
    },
  };

  const fetchCategories = React.useCallback(async () => {
    if (!campaignId) return;
    const { data } = await client.get("/api/v1/campaigns/yml/categories", {
      params: { campaign_id: campaignId, page: 1, limit: 100 },
    });
    const rows = (data?.data || []).map((r) => ({
      id: r.id,
      yml_category_id: r.yml_category_id,
      parent_yml_category_id: r.parent_yml_category_id,
      name: r.name,
      bid: Number(r.bid),
      enabled: Boolean(r.enabled),
      offers_count: Number(r.offers_count || 0),
    }));
    setCategories(rows);
  }, [campaignId]);

  React.useEffect(() => {
    if (demoMode || !campaignId) return;
    if (ymlOriginalFeedUrl) setFeedUrl(ymlOriginalFeedUrl);
    if (ymlReferralFeedUrl) setReferralFeedUrl(ymlReferralFeedUrl);
  }, [demoMode, campaignId, ymlOriginalFeedUrl, ymlReferralFeedUrl]);

  React.useEffect(() => {
    if (demoMode || !campaignId) return;
    if (!ymlOriginalFeedUrl || !ymlReferralFeedUrl) {
      ymlBootstrapDoneRef.current = false;
      return;
    }
    const key = `${campaignId}|${ymlOriginalFeedUrl}|${ymlReferralFeedUrl}`;
    if (ymlBootstrapDoneRef.current === key) return;
    ymlBootstrapDoneRef.current = key;
    fetchCategories().catch((e) => {
      enqueueSnackbar(e?.message || "Не удалось загрузить категории", {
        variant: "error",
      });
    });
  }, [
    demoMode,
    campaignId,
    ymlOriginalFeedUrl,
    ymlReferralFeedUrl,
    fetchCategories,
    enqueueSnackbar,
  ]);

  const copyToClipboard = (value, okMsg = "Скопировано") => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => enqueueSnackbar(okMsg, { variant: "success" }),
      () => enqueueSnackbar("Не удалось скопировать", { variant: "error" })
    );
  };

  const handleLoadDemo = () => {
    setLoading(true);
    setCategories([]);
    window.setTimeout(() => {
      setCategories(
        DEMO_FEED_CATEGORIES.map((c) => ({
          ...c,
          enabled: true,
          bid: 42,
          offers_count: Math.floor(50 + Math.random() * 950),
        }))
      );
      setLoading(false);
    }, 1200);
  };

  const handleParseAndLoad = async () => {
    if (demoMode) return handleLoadDemo();
    if (!campaignId) {
      enqueueSnackbar("Сначала создайте кампанию, затем загрузите прайс лист.", {
        variant: "info",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.post("/api/v1/campaigns/yml/parse", {
        campaign_id: campaignId,
        feed_url: feedUrl,
      });
      setReferralFeedUrl(data?.referral_feed_url || "");
      ymlBootstrapDoneRef.current = `${campaignId}|${feedUrl}|${data?.referral_feed_url || ""}`;
      await fetchCategories();
      enqueueSnackbar("Прайс лист обработан, категории загружены", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.message || "Не удалось загрузить прайс лист", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const scheduleManage = (id, patch) => {
    if (demoMode || !campaignId) return;
    pendingManageRef.current[id] = {
      ...(pendingManageRef.current[id] || {}),
      ...patch,
    };
    if (manageTimerRef.current) window.clearTimeout(manageTimerRef.current);
    manageTimerRef.current = window.setTimeout(async () => {
      const byId = pendingManageRef.current;
      pendingManageRef.current = {};
      const items = Object.entries(byId).map(([rowId, p]) => ({
        id: rowId,
        ...(p.enabled !== undefined ? { enabled: p.enabled } : {}),
        ...(p.bid !== undefined ? { bid: p.bid } : {}),
      }));
      if (!items.length) return;
      try {
        await client.post("/api/v1/campaigns/yml/categories/manage", {
          campaign_id: campaignId,
          items,
        });
      } catch (e) {
        enqueueSnackbar(e?.message || "Не удалось сохранить изменения", {
          variant: "error",
        });
        fetchCategories().catch(() => {});
      }
    }, 600);
  };

  const toggleCategory = (id, enabled) => {
    setCategories((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...row, enabled, bid: enabled && !row.bid ? 42 : row.bid }
          : row
      )
    );
    scheduleManage(id, { enabled });
  };

  const displaySavedOriginal = ymlOriginalFeedUrl || feedUrl;
  const displaySavedReferral = ymlReferralFeedUrl || referralFeedUrl;
  const feedStatus = getFeedStatusMeta(ymlFeedModerateStatus);
  const isMobileFeed = w < 768;
  const feedThumbSize = isMobileFeed ? 56 : 64;
  const hasConnectedFeed = Boolean(displaySavedOriginal?.trim());

  const inputOutlineSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      fontSize: "14px",
      "& fieldset": { borderColor: "#d1d5db" },
      "&:hover fieldset": { borderColor: "#9ca3af" },
    },
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#3b82f6",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#3b82f6",
    },
  };

  const isRejected =
    !demoMode &&
    campaignBroadcastStatus === "NOT_DELIVERING";

  const issuesText = Array.isArray(campaignIssues) ? campaignIssues.join(" ") : "";
  const isCreationNotFinished =
    issuesText.toLowerCase().includes("создание кампании не закончено");

  const feedRejected = ymlFeedModerateStatus === "REJECTED";
  const showRejectionForModeration =
    !demoMode &&
    !isCreationNotFinished &&
    (feedRejected || (isRejected && campaignStatus === "ACTIVE"));

  const handleResendToModeration = async () => {
    if (!campaignId) {
      enqueueSnackbar("Сначала создайте кампанию.", {
        variant: "info",
        autoHideDuration: 2500,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }
    setResendLoading(true);
    try {
      await client.post(`/api/v1/campaigns/${campaignId}/moderation/resend`, {});
      enqueueSnackbar("Кампания повторно отправлена на модерацию", {
        variant: "success",
        autoHideDuration: 2500,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } catch (e) {
      enqueueSnackbar(e?.message || "Не удалось отправить на модерацию", {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {demoMode && (
        <div style={{ ...styles.infoNotification, marginBottom: 16 }}>
          <div style={styles.infoNotificationContent}>
            <span style={{ fontSize: "18px", flexShrink: 0 }}>ℹ️</span>
            <span style={styles.infoText}>
              Демо-превью по параметру{" "}
              <code style={{ fontSize: "0.9em" }}>product_feed_demo=1</code> — кампанию
              создавать не нужно. Уберите параметр из URL для обычного сценария.
            </span>
          </div>
        </div>
      )}

      <div
        style={{
          borderRadius: 10,
          background: "#fff",
          border: `1px solid ${hasConnectedFeed ? feedStatus.border : "#e4e4e7"}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          overflow: "hidden",
          marginBottom: 24,
        }}
      >
        {hasConnectedFeed && (
          <div
            style={{
              display: "flex",
              flexDirection: isMobileFeed ? "column" : "row",
              gap: isMobileFeed ? 12 : 16,
              padding: isMobileFeed ? "14px" : "16px 20px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                width: feedThumbSize,
                height: feedThumbSize,
                borderRadius: 8,
                background: "#eef3ff",
                border: "1px solid #dbe4ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ListAltOutlinedIcon sx={{ fontSize: isMobileFeed ? 24 : 28, color: "#3F73F9" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: isMobileFeed ? 15 : 16, color: "#18181b", marginBottom: 4 }}>
                Прайс‑лист (YML)
              </div>
              <div
                style={{
                  fontSize: isMobileFeed ? 12 : 13,
                  color: "#71717a",
                  marginBottom: 6,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={displaySavedOriginal}
              >
                {truncateFeedUrl(displaySavedOriginal)}
              </div>
              <div style={{ fontSize: isMobileFeed ? 11 : 12, color: "#a1a1aa" }}>
                {displaySavedReferral ? "Основной и реферальный фид" : "YML фид"}
                {categories.length > 0 ? ` · ${categories.length} категорий` : ""}
              </div>
              {feedRejected && (
                <div style={{ marginTop: 8, fontSize: isMobileFeed ? 11 : 12, color: "#dc2626", lineHeight: 1.4 }}>
                  Прайс‑лист отклонён. Обновите фид и отправьте на модерацию повторно.
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: isMobileFeed ? "row" : "column",
                alignItems: isMobileFeed ? "center" : "flex-end",
                justifyContent: isMobileFeed ? "space-between" : "flex-start",
                gap: 10,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 20,
                  fontSize: isMobileFeed ? 11 : 12,
                  fontWeight: 600,
                  color: feedStatus.color,
                  background: feedStatus.bg,
                  whiteSpace: "nowrap",
                }}
              >
                {feedStatus.label}
              </span>
              <a
                href={displaySavedOriginal}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: isMobileFeed ? "6px 10px" : "8px 14px",
                  borderRadius: 6,
                  background: "#DFE8FF",
                  color: "#3F73F9",
                  fontSize: isMobileFeed ? 12 : 13,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                <OpenInNewOutlinedIcon sx={{ fontSize: isMobileFeed ? 16 : 18 }} />
                Открыть
              </a>
            </div>
          </div>
        )}

        <div style={{ padding: isMobileFeed ? "14px" : "16px 20px" }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#18181b", marginBottom: 8 }}>
            {hasConnectedFeed ? "Обновить прайс‑лист" : "Подключение прайс‑листа"}
          </div>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "13px", lineHeight: 1.5 }}>
            Укажите URL YML/XML прайс листа.
            {demoMode && " В демо используются тестовые данные."}
          </Typography>
          <TextField
            disabled={disable}
            fullWidth
            label="Ссылка на прайс лист"
            value={feedUrl}
            onChange={(e) => setFeedUrl(e.target.value)}
            sx={{ mb: 2, ...inputOutlineSx }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="contained"
              disabled={disable || loading || !feedUrl.trim()}
              onClick={handleParseAndLoad}
              sx={{
                bgcolor: "#3F73F9",
                "&:hover": { bgcolor: "#2563eb" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                py: 0.9,
                px: 2,
                boxShadow: "none",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {loading ? "Выгрузка..." : hasConnectedFeed ? "Обновить прайс лист" : "Выгрузить прайс лист"}
            </Button>
            {allowResendToModeration && isRejected && (
              <Button
                variant="outlined"
                disabled={disable || resendLoading}
                onClick={handleResendToModeration}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 0.9,
                  px: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {resendLoading ? "Отправка..." : "Повторно на модерацию"}
              </Button>
            )}
          </Box>
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <CircularProgress size={24} sx={{ color: "#3F73F9" }} />
              <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "14px" }}>
                Выгрузка прайс листа...
              </Typography>
            </Box>
          )}
          {showRejectionForModeration && !feedRejected && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
              <Typography variant="body2" sx={{ color: "#9a3412", fontSize: "13px", fontWeight: 600 }}>
                {`Кампания отклонена.${Array.isArray(campaignIssues) && campaignIssues.length > 0 ? ` ${String(campaignIssues.join(" "))}` : ""}`}
              </Typography>
            </Box>
          )}
        </div>

        {!loading && categories.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setCategoriesExpanded((v) => !v)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: isMobileFeed ? "10px 14px" : "12px 20px",
                border: "none",
                borderTop: "1px solid #f0f0f0",
                background: "#fafafa",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: "#71717a",
                  transform: categoriesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
              <span style={{ fontWeight: 600, fontSize: 13, color: "#18181b" }}>Категории</span>
              <span style={{ fontSize: 12, color: "#a1a1aa" }}>· {categories.length}</span>
            </button>
            <Collapse in={categoriesExpanded}>
              <div style={{ padding: isMobileFeed ? "0 14px 14px" : "0 20px 16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: "10px",
                    marginBottom: "4px",
                    borderBottom: "1px solid #e5e7eb",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#6b7280",
                  }}
                >
                  <span style={{ flex: 1 }}>Категория</span>
                  <span style={{ width: 80, textAlign: "center" }}>Статус</span>
                  <span style={{ width: 120, textAlign: "right" }}>Кол-во товаров</span>
                </div>
                <Box
                  sx={{
                    maxHeight: { xs: "min(50vh, 320px)", sm: 400 },
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  {categories.map((row, idx) => (
                    <div
                      key={row.id}
                      style={{
                        ...(idx > 0
                          ? { borderTop: "1px solid #e5e7eb", paddingTop: "12px", marginTop: "12px" }
                          : { paddingTop: "4px" }),
                      }}
                    >
                      <div style={{ ...styles.switchContainer, marginBottom: 0 }}>
                        <span style={{ flex: 1, fontSize: "14px", color: "#1f2937", paddingRight: "8px" }}>
                          {row.name}
                        </span>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                          <Switch
                            disabled={disable}
                            checked={row.enabled}
                            onChange={(e) => toggleCategory(row.id, e.target.checked)}
                            size="small"
                            sx={switchSx}
                          />
                          <Typography
                            variant="body2"
                            sx={{ width: 120, textAlign: "right", color: "#6b7280", fontSize: "13px" }}
                          >
                            {row.offers_count ?? 0}
                          </Typography>
                        </Box>
                      </div>
                    </div>
                  ))}
                </Box>
              </div>
            </Collapse>
          </>
        )}
      </div>

      {showNextButton && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "32px" }}>
          <LoadingButton
            loadingPosition="end"
            variant="contained"
            sx={{
              marginLeft: "32px",
            }}
            disabled={disable}
            onClick={onNext}
            endIcon={<SendIcon />}
          >
            Далее
          </LoadingButton>
        </div>
      )}

      {showAdminRedirect && (displaySavedOriginal || displaySavedReferral) && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Для администратора
          </Typography>
          {displaySavedReferral && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ minWidth: 140, fontSize: "13px", color: "#6b7280" }}>
                Реферальный прайс лист
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={displaySavedReferral}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, minWidth: 180, ...inputOutlineSx, "& .MuiInputBase-input": { fontSize: "12px" } }}
              />
              <IconButton
                size="small"
                onClick={() => copyToClipboard(displaySavedReferral, "Реферальная ссылка скопирована")}
                aria-label="Копировать реферальный URL"
                sx={{ color: "#3F73F9", border: "1px solid #e5e7eb", borderRadius: "6px" }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
}

function CompanyContainer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProductFeedDemo =
    searchParams.get("product_feed_demo") === "1";
  const [value, setValue] = React.useState("1");
  const { id, editCampaign, bootstrap_retargeting_campaign_kind } = useSelector(
    selectCreateCampaigns.all_object
  );
  const campaignRetargetingKind =
    editCampaign?.basic_config?.retargeting_campaign_kind ??
    bootstrap_retargeting_campaign_kind;
  const isFeedOnlyCampaign = campaignRetargetingKind === "FEED";
  const isHybridCampaign = campaignRetargetingKind === "DYNAMIC_FEED";
  const hasFeed =
    isFeedOnlyCampaign ||
    isHybridCampaign ||
    editCampaign?.basic_config?.target_type === "PRODUCT" ||
    isProductFeedDemo;
  const isProductCampaign = hasFeed;
  const dispatch = useDispatch();
  const productFeedDemoNavigated = React.useRef(false);

  React.useEffect(() => {
    if (!isProductFeedDemo || productFeedDemoNavigated.current) return;
    productFeedDemoNavigated.current = true;
    setValue("3");
    const isEdit = Boolean(editCampaign);
    dispatch(
      setCurrentPage({
        id: isEdit ? 17 : 11,
        route: "/add-company",
        meta: {
          name: isEdit
            ? "Редактировать кампанию — прайс лист"
            : "Добавить кампанию — прайс лист",
          step: "3",
          mode: isEdit ? "edit" : "create",
        },
      })
    );
  }, [isProductFeedDemo, editCampaign, dispatch]);

  const [currentAdId, setCurrentAdId] = React.useState(null);
  const [upd, setUpd] = React.useState(false)
  const [updBanner, setUpdBanner] = React.useState(false)
  
  const [isMobile, setIsMobile] = React.useState(window.outerWidth < 768);
  const { user } = useUser();

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.outerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const saveBannersFormRef = React.useRef(() => {});
  const currentAdIdRef = React.useRef(currentAdId);
  
  let mediaPlanExpired = false;
  if (editCampaign && editCampaign.media_plan?.date_end) {
    var d1 = new Date();
    var d2 = new Date(editCampaign.media_plan.date_end);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    if (d2.getTime() < d1.getTime()) {
      mediaPlanExpired = true;
    }
  }
  const disable = mediaPlanExpired;

  const handleChange = (event, newValue) => {
    if (newValue < LAST_TAB) setValue(newValue);
    else navigate("/");
    // Обновляем контекст страницы (создание или редактирование + шаг)
    const isEdit = Boolean(editCampaign);
    const map = {
      "1": isEdit ? { id: 15, name: "Редактировать кампанию — основные настройки" } : { id: 9, name: "Добавить кампанию — основные настройки" },
      "2": isEdit ? { id: 16, name: "Редактировать кампанию — медиаплан" } : { id: 10, name: "Добавить кампанию — медиаплан" },
      "3":
        isEdit
          ? {
              id: 17,
              name: isProductCampaign
                ? "Редактировать кампанию — прайс лист"
                : "Редактировать кампанию — объявление",
            }
          : {
              id: 11,
              name: isProductCampaign
                ? "Добавить кампанию — прайс лист"
                : "Добавить кампанию — объявление",
            },
      "4": isEdit ? { id: 18, name: "Редактировать кампанию — ретаргетинг" } : { id: 12, name: "Добавить кампанию — ретаргетинг" },
      "5": isEdit ? { id: 19, name: "Редактировать кампанию — оптимизация" } : { id: 13, name: "Добавить кампанию — оптимизация" },
      "6": isEdit ? { id: 20, name: "Редактировать кампанию — аудитория" } : { id: 14, name: "Добавить кампанию — аудитория" },
    };
    const page = map[String(newValue)] || {};
    dispatch(setCurrentPage({ id: page.id, route: "/add-company", meta: { name: page.name, step: String(newValue), mode: isEdit ? "edit" : "create" } }));
  };

  // Устанавливаем контекст для шага "Основные настройки" при открытии страницы и
  // обновляем при смене режима (создание/редактирование)
  React.useEffect(() => {
    if (value !== "1") return;
    const isEdit = Boolean(editCampaign);
    const page = isEdit
      ? { id: 15, name: "Редактировать кампанию — основные настройки" }
      : { id: 9, name: "Добавить кампанию — основные настройки" };
    dispatch(setCurrentPage({ id: page.id, route: "/add-company", meta: { name: page.name, step: "1", mode: isEdit ? "edit" : "create" } }));
  }, [value, editCampaign]);

  React.useEffect(() => {
    currentAdIdRef.current = currentAdId;
  }, [currentAdId]);

  const openAd = async (newId, isStatus = false, skipUnsavedCheck = false) => {
    if (isStatus) {
      return await saveBannersFormRef.current(currentAdIdRef.current);
    }
    if (!skipUnsavedCheck && hasUnsavedChanges && newId != null) {
      const confirmSave = window.confirm("Вы не сохранили объявление, сохранить?");
      if (confirmSave) {
        await saveBannersFormRef.current(currentAdIdRef.current);
      }
    }
    setCurrentAdId(newId);
  };

  return (
    <div className="wraper">
      <Box sx={{ typography: "body1" }}>
        <TabContext value={value}>
          <Box
            className="tes"
            sx = {(window.outerWidth > 1000) ? 
              {boxShadow: 3,
              width: "100%",
              borderColor: "divider",
              padding: "4px",
              borderTopLeftRadius: "25px",
              borderTopRightRadius: "25px",
            } : (window.outerWidth < 768) ?  {
              width: "343", 
              backgroundColor: "white",
              height: "55px",
              marginLeft: "-30px",
              // overflowX: "scroll",
            } : {
              width: "670px", 
              height: "55px",
            }}
          >
            <TabList
              onChange={handleChange}
              aria-label="lab API tabs example"
              variant="scrollable"
              scrollButtons="auto"
              sx={(window.outerWidth > 1000) ? {
                display: "flex",
                padding: "15px",
                marginLeft: "38px",
              } : (window.outerWidth < 768) ?  {
                display: "flex",
                // flexDirection: "row",
                // overflowX: "scroll",
                width: "343px", 
                height: "100%",
                marginLeft: "10px",
                position: "relative",
                typography: "h5"
                // padding: "15px",
                // marginLeft: "38px",
              } : {
                display: "flex",
                background: "#f0f1f3",
                borderRadius: '10px',
                width: "670px", 
                height: "100%",
                position: "relative",
                typography: "h6"
              }}
            >
              <Tab
                icon={<DataSaverOffIcon />}
                iconPosition="start"
                label="Основные настройки"
                value="1"
                sx={{}}
              />

              <Tab
                icon={<DataSaverOffIcon />}
                iconPosition="start"
                label="Медиа план"
                value="2"
                disabled={!id && +value < 2}
                sx={(window.outerWidth > 1000) ? {
                  marginLeft: "80px",
                } : {}}
              />

              <Tab
                icon={<DataSaverOffIcon />}
                iconPosition="start"
                label={
                  isFeedOnlyCampaign
                    ? "Прайс лист"
                    : isHybridCampaign
                      ? "Объявления + прайс лист"
                      : "Объявления"
                }
                value="3"
                disabled={!isProductFeedDemo && !id && +value < 4}
                sx={(window.outerWidth > 1000) ? {
                  marginLeft: "80px",
                } : {}}
              />

              {(!user.is_trial || user.is_trial === false) && (
                <Tab
                  icon={<DataSaverOffIcon />}
                  iconPosition="start"
                  label="Ретаргетинг"
                  value="4"
                  disabled={!id && +value < 3}
                  sx={(window.outerWidth > 1000) ? {
                    marginLeft: "80px",
                  } : {}}
                />
              )}


              <Tab
                icon={<DataSaverOffIcon />}
                iconPosition="start"
                label="Оптимизация"
                value="5"
                disabled={!id && +value < 5}
                sx={(window.outerWidth > 1000) ? {
                  marginLeft: "80px",
                } : {}}
              />

              <Tab
                icon={<DataSaverOffIcon />}
                iconPosition="start"
                label="Аудитория"
                value="6"
                disabled={!id && +value < 6}
                sx={(window.outerWidth > 1000) ? {
                  marginLeft: "80px",
                } : {}}
              />
            </TabList>
          </Box>
          <TabPanel
            className="panel"
            value="1"
            style={(window.outerWidth > 767) ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 25 } : {margin: "-40px", marginTop: "10px"} }
          >
            <FirstSettings
              handleChange={handleChange}
              campaignID={id}
              isNew={!id}
              data={editCampaign ? editCampaign.basic_config : null}
              mediaPlan={editCampaign ? editCampaign.media_plan : null}
              disable={disable}
            />
          </TabPanel>
          <TabPanel
            value="2"
            className="panel"
            style={(window.outerWidth > 767) ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 25 } : {margin: "-40px"}}
          >
            <MediaPlan
              handleChange={handleChange}
              campaignID={id}
              data={editCampaign ? editCampaign.media_plan : null}
              disable={disable}
            />
          </TabPanel>
          <TabPanel
            value="3"
            className="panel"
            style={(window.outerWidth > 767) ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 25 } : {margin: "20px -15px", padding: "0"}}
          >
            {isFeedOnlyCampaign ? (
              <ProductFeedSection
                campaignId={id}
                disable={disable}
                demoMode={isProductFeedDemo}
                campaignBroadcastStatus={
                  editCampaign?.broadcast_status ??
                  editCampaign?.basic_config?.broadcast_status ??
                  null
                }
                campaignStatus={
                  editCampaign?.status ??
                  editCampaign?.basic_config?.status ??
                  null
                }
                campaignIssues={
                  editCampaign?.issues ??
                  editCampaign?.basic_config?.issues ??
                  []
                }
                ymlFeedModerateStatus={
                  editCampaign?.yml_feed_moderate_status ??
                  editCampaign?.basic_config?.yml_feed_moderate_status ??
                  null
                }
                onNext={() => handleChange(null, "4")}
                ymlOriginalFeedUrl={
                  editCampaign?.yml_original_feed_url ??
                  editCampaign?.basic_config?.yml_original_feed_url ??
                  ""
                }
                ymlReferralFeedUrl={
                  editCampaign?.yml_referral_feed_url ??
                  editCampaign?.basic_config?.yml_referral_feed_url ??
                  ""
                }
                allowResendToModeration
                showNextButton={false}
              />
            ) : isHybridCampaign ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <ProductFeedSection
                  campaignId={id}
                  disable={disable}
                  demoMode={isProductFeedDemo}
                  campaignBroadcastStatus={
                    editCampaign?.broadcast_status ??
                    editCampaign?.basic_config?.broadcast_status ??
                    null
                  }
                  campaignStatus={
                    editCampaign?.status ??
                    editCampaign?.basic_config?.status ??
                    null
                  }
                  campaignIssues={
                    editCampaign?.issues ??
                    editCampaign?.basic_config?.issues ??
                    []
                  }
                  ymlFeedModerateStatus={
                    editCampaign?.yml_feed_moderate_status ??
                    editCampaign?.basic_config?.yml_feed_moderate_status ??
                    null
                  }
                  onNext={() => handleChange(null, "4")}
                  ymlOriginalFeedUrl={
                    editCampaign?.yml_original_feed_url ??
                    editCampaign?.basic_config?.yml_original_feed_url ??
                    ""
                  }
                  ymlReferralFeedUrl={
                    editCampaign?.yml_referral_feed_url ??
                    editCampaign?.basic_config?.yml_referral_feed_url ??
                    ""
                  }
                  allowResendToModeration={false}
                  showNextButton={false}
                />
                <Ads
                  campaignId={id}
                  currentAdId={currentAdId}
                  openAd={openAd}
                  upd={upd}
                  setUpd={setUpd}
                  updBanner={updBanner}
                  setUpdBanner={setUpdBanner}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  hasUnsavedChanges={hasUnsavedChanges}
                  saveFormRef={saveBannersFormRef}
                  isMobile={isMobile}
                  changeTab={handleChange}
                  disable={false}
                  mediaPlanExpired={mediaPlanExpired}
                  showNextButton={false}
                />
              </div>
            ) : (
              <Ads
                campaignId={id}
                currentAdId={currentAdId}
                openAd={openAd}
                upd={upd}
                setUpd={setUpd}
                updBanner={updBanner}
                setUpdBanner={setUpdBanner}
                setHasUnsavedChanges={setHasUnsavedChanges}
                hasUnsavedChanges={hasUnsavedChanges}
                saveFormRef={saveBannersFormRef}
                isMobile={isMobile}
                changeTab={handleChange}
                disable={false}
                mediaPlanExpired={mediaPlanExpired}
                showNextButton
              />
            )}

            {(isFeedOnlyCampaign || isHybridCampaign) && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, px: 2, pb: 2 }}>
                <LoadingButton
                  loadingPosition="end"
                  variant="contained"
                  disabled={disable}
                  onClick={() => handleChange(null, "4")}
                  endIcon={<SendIcon />}
                >
                  Далее
                </LoadingButton>
              </Box>
            )}
          </TabPanel>
          <TabPanel
            value="4"
            className="panel"
            style={(window.outerWidth > 767) ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 25 } : {margin: "-40px"}}
          >
            <RetargetingPage
              changeTab={handleChange}
              campaignID={id}
              data={editCampaign ? editCampaign.retargeting : null}
              disable={disable}
            />
          </TabPanel>
          <TabPanel
            className="panel"
            value="5"
            style={(window.outerWidth > 767) ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 25 } : {margin: "-40px"}}
          >
            <Optimization
              handleChange={handleChange}
              campaignID={id}
              data={editCampaign ? editCampaign : null}
              disable={false}
              isTrial={user.is_trial}
            />
          </TabPanel>
          <TabPanel
            className="panel"
            value="6"
            style={(window.outerWidth > 767) ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 25 } : {margin: "-40px"}}
          >
            <Audience handleChange={handleChange} 
              campaignID={id} 
              data={editCampaign ? editCampaign.new_segment_id : ""}
              disable={disable}
            />
          </TabPanel>
        </TabContext>
      </Box>
      {/* {inActive && <p>RED</p>} */}
    </div>
  );
}

export default CompanyContainer;
